import type { BaseQueryFn } from '@reduxjs/toolkit/query';

import { accessTokenRefreshed, sessionExpired } from '../auth/authSlice';
import { getApiClientRuntime } from '../config';
import { isApiError, networkError, type ApiError } from './apiError';
import { apiFetch, isAuthPath, type ApiRequestArgs, type EnvelopeMeta } from './http';
import { refreshAccessToken } from './refresh';

type BaseQueryState = { auth: { accessToken: string | null } };

function tokenOf(state: unknown): string | null {
  const auth = (state as Partial<BaseQueryState>).auth;
  return auth?.accessToken ?? null;
}

/**
 * The one base query.
 *
 * It attaches the in-memory bearer token, appends `?lang=`, sends the refresh
 * cookie on `/auth/*` only, unwraps the `{ data, meta }` envelope, feeds
 * `meta.serverTime` to the clock anchor, and on a 401 retries the request
 * exactly once behind the single shared refresh promise.
 *
 * `meta` is handed to `transformResponse` as its second argument, which is
 * how a paginated endpoint reaches `page` / `total` without the envelope
 * leaking into component code.
 *
 * The 4th generic — `DefinitionExtraOptions` — is deliberately
 * `Record<string, unknown>`, not `Record<string, never>`. RTK Query decides
 * whether an injected endpoint's `extraOptions` field is required by asking
 * "does this type have any non-optional keys" (`HasRequiredProps` in its own
 * source); `Record<string, never>` answers yes (its index signature has no
 * `undefined` in its value type, so every key looks required), which is what
 * made `extraOptions` mandatory on every `build.query`/`build.mutation` in
 * this repo and broke this file's own README example. `Record<string,
 * unknown>` has no required keys, so the field becomes optional — an
 * endpoint that never uses it just omits it — while `{}` object literals
 * still using `extraOptions: {}` (there's no reason to remove it after this
 * fix; it isn't needed) keep compiling too. `{}` itself was avoided as the
 * type: `@typescript-eslint/no-empty-object-type` (in this repo's
 * `tseslint.configs.recommended`) flags it.
 */
export const baseQueryWithReauth: BaseQueryFn<
  ApiRequestArgs | string,
  unknown,
  ApiError,
  Record<string, unknown>,
  EnvelopeMeta
> = async (rawArgs, api) => {
  const args: ApiRequestArgs = typeof rawArgs === 'string' ? { url: rawArgs } : rawArgs;

  const attempt = async (token: string | null): Promise<{ data: unknown; meta: EnvelopeMeta }> => {
    const signal: AbortSignal | undefined = api.signal;
    const result = await apiFetch(args, { token, ...(signal ? { signal } : {}) });
    return { data: result.data, meta: result.meta };
  };

  try {
    return await attempt(tokenOf(api.getState()));
  } catch (first) {
    if (!isApiError(first)) return { error: networkError(first) };

    // A 401 from an auth route is the answer, not a reason to refresh: the
    // refresh route answering 401 IS the expired session.
    if (first.status !== 401 || isAuthPath(args.url)) return { error: first };

    const { token, offline } = await refreshAccessToken();
    if (token === null) {
      /*
       * Only a refusal ends a session. The server answered 401 a moment ago
       * and the link has since gone (a phone walking into a cold room); that
       * says nothing about the refresh cookie, and signing out on it would
       * throw away a session the next refresh will likely renew — along with,
       * in the worker PWA, the user id every queued punch is scoped to.
       */
      if (!offline) {
        api.dispatch(sessionExpired());
        getApiClientRuntime().onSessionExpired();
      }
      return { error: first };
    }
    api.dispatch(accessTokenRefreshed(token));

    try {
      return await attempt(token);
    } catch (second) {
      return { error: isApiError(second) ? second : networkError(second) };
    }
  }
};
